import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  Header,
  FormHeader,
  ScreenLayout,
  colors,
  spacing,
  typography,
} from '../components';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { CalendarPickerField } from '../components/CalendarPickerField';
import { Modal, ConfirmationModal } from '../components/Modal';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useGoBack } from '../hooks/useGoBack';

type VisitOutcomeActionType = 'reschedule' | 'reminder' | 'assign' | 'note';

interface VisitOutcomeAction {
  type: VisitOutcomeActionType;
  config?: Record<string, any>;
}

interface VisitOutcome {
  id: string;
  label: string;
  labelAr?: string;
  icon?: string;
  color?: string;
  resultType: 'success' | 'neutral' | 'failure';
  action?: VisitOutcomeAction;
  order: number;
  isActive: boolean;
}

interface VisitDefaults {
  expectedDurationMinutes: number;
  dailyVisitTarget: number;
  defaultCampaignDurationDays: number;
  defaultVisitWindowHours: number;
}
interface VisitLocationEnforcement {
  enabled: boolean;
  radiusMeters: number;
  maxAccuracyMeters: number;
  allowOverrideWithReason: boolean;
}
interface VisitPhotoEnforcement {
  requireOnStart: boolean;
  requireOnEnd: boolean;
  cameraOnly: boolean;
  minPhotosOnStart: number;
  minPhotosOnEnd: number;
  maxPhotos: number;
}
interface VisitEligibility {
  eligibleUserIds: string[];
  eligibleDepartmentIds: string[];
}
interface VisitsServiceConfig {
  defaults: VisitDefaults;
  location: VisitLocationEnforcement;
  photos: VisitPhotoEnforcement;
  eligibility: VisitEligibility;
  outcomes: VisitOutcome[];
  calendar: { autoCreateEvent: boolean; defaultCalendarId?: string; reminderOffsets: number[] };
  routing?: { engineProvider: 'haversine' | 'osrm'; defaultMode: 'driving' | 'walking' | 'cycling' | 'motorcycle' };
  status: 'active' | 'inactive';
  connectedAt: string;
  updatedAt?: string;
}

const ICON_CHOICES: string[] = [
  'checkmark-circle-outline',
  'close-circle-outline',
  'home-outline',
  'calendar-outline',
  'alarm-outline',
  'thumbs-up-outline',
  'thumbs-down-outline',
  'help-circle-outline',
  'person-outline',
  'flag-outline',
];

const emptyOutcomeAction = (type: VisitOutcomeActionType): VisitOutcomeAction => {
  if (type === 'reschedule') return { type, config: { defaultOffsetHours: 24 } };
  if (type === 'reminder') return { type, config: { defaultOffsetMinutes: 1440 } };
  if (type === 'assign') return { type, config: { defaultDepartmentId: undefined } };
  return { type, config: { placeholder: '', required: false } };
};

export const VisitsSetupScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  // Reached from multiple places now (Home's Complete Profile widget, the
  // Visits list's settings icon, and Business Permissions) — Back should
  // return to wherever the user actually came from, not always jump to
  // Permissions.
  const goBack = useGoBack('business-permissions');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<VisitsServiceConfig | null>(null);

  const [defaults, setDefaults] = useState<VisitDefaults | null>(null);
  const [location, setLocation] = useState<VisitLocationEnforcement | null>(null);
  const [photos, setPhotos] = useState<VisitPhotoEnforcement | null>(null);
  const [eligibility, setEligibility] = useState<VisitEligibility | null>(null);
  const [outcomes, setOutcomes] = useState<VisitOutcome[]>([]);
  const [calendarCfg, setCalendarCfg] = useState<{ autoCreateEvent: boolean; defaultCalendarId?: string; reminderOffsets: number[] } | null>(null);
  const [reminderOffsetsText, setReminderOffsetsText] = useState('');
  // Route engine — 'osrm' additionally requires the SERVER to have
  // ROUTE_ENGINE_PROVIDER=osrm and OSRM_BASE_URL set (an env-level
  // deployment capability, not something this screen controls) — this
  // toggle is the business-level PREFERENCE that only takes effect once
  // that server capability actually exists. Defaults to 'haversine' (never
  // silently 'osrm') so a business isn't switched onto a routing engine the
  // deployment hasn't actually provisioned.
  const [routing, setRouting] = useState<{ engineProvider: 'haversine' | 'osrm'; defaultMode: 'driving' | 'walking' | 'cycling' | 'motorcycle' }>({
    engineProvider: 'haversine',
    defaultMode: 'driving',
  });

  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showDeptPicker, setShowDeptPicker] = useState(false);
  const [showAssignDeptPicker, setShowAssignDeptPicker] = useState(false);

  const [editingOutcome, setEditingOutcome] = useState<VisitOutcome | null>(null);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcomeToRemove, setOutcomeToRemove] = useState<VisitOutcome | null>(null);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const syncLocalState = (cfg: VisitsServiceConfig) => {
    setDefaults(cfg.defaults);
    setLocation(cfg.location);
    setPhotos(cfg.photos);
    setEligibility(cfg.eligibility);
    setOutcomes([...cfg.outcomes].sort((a, b) => a.order - b.order));
    setCalendarCfg(cfg.calendar);
    setReminderOffsetsText((cfg.calendar.reminderOffsets || []).join(', '));
    if (cfg.routing) setRouting(cfg.routing);
  };

  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiService.getVisitsConfig();
      const cfg = (res?.data ?? null) as VisitsServiceConfig | null;
      setConfig(cfg);
      if (cfg) syncLocalState(cfg);
    } catch (err) {
      console.warn('[VisitsSetup] loadConfig error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleEnable = async () => {
    setIsSaving(true);
    try {
      const res = await apiService.updateVisitsConfig({});
      const cfg = (res?.data ?? null) as VisitsServiceConfig | null;
      if (cfg) {
        setConfig(cfg);
        syncLocalState(cfg);
      }
      showNotification('success', t('visitsSetup.title'), t('visitsSetup.savedSuccess'));
    } catch (err: any) {
      showNotification('error', t('visitsSetup.title'), err.message ?? t('visitsSetup.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePatch = async (patch: Partial<VisitsServiceConfig>) => {
    setIsSaving(true);
    try {
      const res = await apiService.updateVisitsConfig(patch);
      const cfg = (res?.data ?? null) as VisitsServiceConfig | null;
      if (cfg) {
        setConfig(cfg);
        syncLocalState(cfg);
      }
      showNotification('success', t('visitsSetup.title'), t('visitsSetup.savedSuccess'));
    } catch (err: any) {
      showNotification('error', t('visitsSetup.title'), err.message ?? t('visitsSetup.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisable = async () => {
    setIsSaving(true);
    try {
      await apiService.disableVisitsService();
      setConfig(null);
      setShowDisableConfirm(false);
      showNotification('success', t('visitsSetup.title'), t('visitsSetup.savedSuccess'));
    } catch (err: any) {
      showNotification('error', t('visitsSetup.title'), err.message ?? t('visitsSetup.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const openAddOutcome = () => {
    const maxOrder = outcomes.reduce((m, o) => Math.max(m, o.order), -1);
    setEditingOutcome({
      id: `outcome-${Date.now()}`,
      label: '',
      icon: ICON_CHOICES[0],
      color: '#DA942C',
      resultType: 'neutral',
      order: maxOrder + 1,
      isActive: true,
    });
    setShowOutcomeModal(true);
  };

  const openEditOutcome = (outcome: VisitOutcome) => {
    setEditingOutcome({ ...outcome });
    setShowOutcomeModal(true);
  };

  const handleSaveOutcome = () => {
    if (!editingOutcome || !editingOutcome.label.trim()) {
      showNotification('warning', t('visitsSetup.title'), t('visitsSetup.outcomes.label'));
      return;
    }
    setOutcomes((prev) => {
      const exists = prev.some((o) => o.id === editingOutcome.id);
      const next = exists
        ? prev.map((o) => (o.id === editingOutcome.id ? editingOutcome : o))
        : [...prev, editingOutcome];
      return next.sort((a, b) => a.order - b.order);
    });
    setShowOutcomeModal(false);
    setEditingOutcome(null);
  };

  const handleRemoveOutcome = () => {
    if (!outcomeToRemove) return;
    setOutcomes((prev) => prev.filter((o) => o.id !== outcomeToRemove.id));
    setOutcomeToRemove(null);
  };

  const moveOutcome = (outcome: VisitOutcome, direction: -1 | 1) => {
    setOutcomes((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((o) => o.id === outcome.id);
      const swapIdx = idx + direction;
      if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return prev;
      const a = sorted[idx];
      const b = sorted[swapIdx];
      const aOrder = a.order;
      a.order = b.order;
      b.order = aOrder;
      return [...sorted].sort((x, y) => x.order - y.order);
    });
  };

  const resultTypeLabel = (rt: VisitOutcome['resultType']) => {
    if (rt === 'success') return t('visitsSetup.outcomes.resultTypeSuccess');
    if (rt === 'failure') return t('visitsSetup.outcomes.resultTypeFailure');
    return t('visitsSetup.outcomes.resultTypeNeutral');
  };

  const actionTypeLabel = (type?: VisitOutcomeActionType) => {
    if (type === 'reschedule') return t('visitsSetup.outcomes.actionReschedule');
    if (type === 'reminder') return t('visitsSetup.outcomes.actionReminder');
    if (type === 'assign') return t('visitsSetup.outcomes.actionAssign');
    if (type === 'note') return t('visitsSetup.outcomes.actionNote');
    return t('visitsSetup.outcomes.actionNone');
  };

  if (isLoading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={false}
        formHeader={<FormHeader title={t('visitsSetup.title')} onBack={goBack} />}
      >
        <View style={styles.screen}>
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={t('visitsSetup.title')} onBack={goBack} />}
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        {!config ? (
          <View style={styles.formCard}>
            <Text style={styles.sectionTitle}>{t('visitsSetup.title')}</Text>
            <Text style={styles.sectionSubtitle}>{t('visitsSetup.enablePrompt')}</Text>
            <TouchableOpacity
              style={[styles.primaryButton, isSaving && styles.disabledButton]}
              onPress={handleEnable}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color={colors.white as string} />
              ) : (
                <Text style={styles.primaryButtonText}>{t('visitsSetup.enableButton')}</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {/* Defaults */}
            {defaults && (
              <View style={styles.formCard}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.defaults')}</Text>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.expectedDurationMinutes')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(defaults.expectedDurationMinutes)}
                  onChangeText={(v) => setDefaults({ ...defaults, expectedDurationMinutes: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.dailyVisitTarget')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(defaults.dailyVisitTarget)}
                  onChangeText={(v) => setDefaults({ ...defaults, dailyVisitTarget: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.defaultCampaignDurationDays')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(defaults.defaultCampaignDurationDays)}
                  onChangeText={(v) => setDefaults({ ...defaults, defaultCampaignDurationDays: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.defaultVisitWindowHours')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(defaults.defaultVisitWindowHours)}
                  onChangeText={(v) => setDefaults({ ...defaults, defaultVisitWindowHours: Number(v) || 0 })}
                />
                <TouchableOpacity
                  style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                  onPress={() => handleSavePatch({ defaults })}
                  disabled={isSaving}
                >
                  <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Location enforcement */}
            {location && (
              <View style={styles.formCard}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.location')}</Text>
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.fields.locationEnabled')}</Text>
                  <Switch
                    value={location.enabled}
                    onValueChange={(v) => setLocation({ ...location, enabled: v })}
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.radiusMeters')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(location.radiusMeters)}
                  onChangeText={(v) => setLocation({ ...location, radiusMeters: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.maxAccuracyMeters')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(location.maxAccuracyMeters)}
                  onChangeText={(v) => setLocation({ ...location, maxAccuracyMeters: Number(v) || 0 })}
                />
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.fields.allowOverride')}</Text>
                  <Switch
                    value={location.allowOverrideWithReason}
                    onValueChange={(v) => setLocation({ ...location, allowOverrideWithReason: v })}
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
                <TouchableOpacity
                  style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                  onPress={() => handleSavePatch({ location })}
                  disabled={isSaving}
                >
                  <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Photo enforcement */}
            {photos && (
              <View style={styles.formCard}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.photos')}</Text>
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.fields.requireOnStart')}</Text>
                  <Switch
                    value={photos.requireOnStart}
                    onValueChange={(v) => setPhotos({ ...photos, requireOnStart: v })}
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.fields.requireOnEnd')}</Text>
                  <Switch
                    value={photos.requireOnEnd}
                    onValueChange={(v) => setPhotos({ ...photos, requireOnEnd: v })}
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.minPhotosOnStart')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(photos.minPhotosOnStart)}
                  onChangeText={(v) => setPhotos({ ...photos, minPhotosOnStart: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.minPhotosOnEnd')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(photos.minPhotosOnEnd)}
                  onChangeText={(v) => setPhotos({ ...photos, minPhotosOnEnd: Number(v) || 0 })}
                />
                <Text style={styles.formLabel}>{t('visitsSetup.fields.maxPhotos')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(photos.maxPhotos)}
                  onChangeText={(v) => setPhotos({ ...photos, maxPhotos: Number(v) || 0 })}
                />
                <View style={styles.lockedRow}>
                  <Ionicons name="camera-outline" size={16} color={colors.text.secondary as string} />
                  <Text style={styles.lockedRowText}>{t('visitsSetup.fields.cameraOnlyNote')}</Text>
                </View>
                <TouchableOpacity
                  style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                  onPress={() => handleSavePatch({ photos })}
                  disabled={isSaving}
                >
                  <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Eligibility */}
            {eligibility && (
              <View style={styles.formCard}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.eligibility')}</Text>
                <Text style={styles.formLabel}>
                  {t('visitsSetup.fields.eligibleUsers')} ({eligibility.eligibleUserIds.length})
                </Text>
                <TouchableOpacity style={styles.pickerButton} onPress={() => setShowUserPicker(true)}>
                  <Text style={styles.pickerButtonText}>{t('visitsSetup.fields.selectUsers')}</Text>
                </TouchableOpacity>
                <Text style={styles.formLabel}>
                  {t('visitsSetup.fields.eligibleDepartments')} ({eligibility.eligibleDepartmentIds.length})
                </Text>
                <TouchableOpacity style={styles.pickerButton} onPress={() => setShowDeptPicker(true)}>
                  <Text style={styles.pickerButtonText}>{t('visitsSetup.fields.selectDepartments')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                  onPress={() => handleSavePatch({ eligibility })}
                  disabled={isSaving}
                >
                  <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Outcomes editor */}
            <View style={styles.formCard}>
              <View style={styles.sectionHeaderRow}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.outcomes')}</Text>
              </View>
              <TouchableOpacity style={styles.addOutcomeButton} onPress={openAddOutcome}>
                <Ionicons name="add-circle-outline" size={18} color={colors.primary as string} />
                <Text style={styles.addOutcomeButtonText}>{t('visitsSetup.outcomes.addOutcome')}</Text>
              </TouchableOpacity>
              {outcomes.map((outcome, idx) => (
                <TouchableOpacity
                  key={outcome.id}
                  style={styles.outcomeRow}
                  onPress={() => openEditOutcome(outcome)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.outcomeSwatch, { backgroundColor: outcome.color || (colors.primary as string) }]} />
                  <Ionicons
                    name={(outcome.icon as any) || 'ellipse-outline'}
                    size={18}
                    color={colors.text.primary as string}
                    style={styles.outcomeIcon}
                  />
                  <View style={styles.outcomeInfo}>
                    <Text style={[styles.outcomeLabel, !outcome.isActive && styles.outcomeLabelInactive]}>
                      {outcome.label}
                    </Text>
                    <Text style={styles.outcomeMeta}>
                      {resultTypeLabel(outcome.resultType)}
                      {outcome.action ? ` · ${actionTypeLabel(outcome.action.type)}` : ''}
                    </Text>
                  </View>
                  <View style={styles.outcomeReorderCol}>
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation?.();
                        moveOutcome(outcome, -1);
                      }}
                      disabled={idx === 0}
                    >
                      <Ionicons name="chevron-up-outline" size={16} color={idx === 0 ? (colors.border as string) : (colors.text.secondary as string)} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation?.();
                        moveOutcome(outcome, 1);
                      }}
                      disabled={idx === outcomes.length - 1}
                    >
                      <Ionicons
                        name="chevron-down-outline"
                        size={16}
                        color={idx === outcomes.length - 1 ? (colors.border as string) : (colors.text.secondary as string)}
                      />
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity
                    onPress={(e) => {
                      e.stopPropagation?.();
                      setOutcomeToRemove(outcome);
                    }}
                  >
                    <Ionicons name="trash-outline" size={18} color={colors.status.error as string} />
                  </TouchableOpacity>
                </TouchableOpacity>
              ))}
              <TouchableOpacity
                style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                onPress={() => handleSavePatch({ outcomes })}
                disabled={isSaving}
              >
                <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
              </TouchableOpacity>
            </View>

            {/* Calendar defaults */}
            {calendarCfg && (
              <View style={styles.formCard}>
                <Text style={styles.sectionTitle}>{t('visitsSetup.sections.calendar')}</Text>
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.fields.autoCreateEvent')}</Text>
                  <Switch
                    value={calendarCfg.autoCreateEvent}
                    onValueChange={(v) => setCalendarCfg({ ...calendarCfg, autoCreateEvent: v })}
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.reminderOffsets')}</Text>
                <TextInput
                  style={styles.input}
                  value={reminderOffsetsText}
                  onChangeText={setReminderOffsetsText}
                  onBlur={() => {
                    const parsed = reminderOffsetsText
                      .split(',')
                      .map((s) => Number(s.trim()))
                      .filter((n) => !Number.isNaN(n) && n >= 0);
                    setCalendarCfg({ ...calendarCfg, reminderOffsets: parsed });
                  }}
                />
                <CalendarPickerField
                  label={t('visitsSetup.fields.defaultCalendarId')}
                  value={calendarCfg.defaultCalendarId}
                  onChange={(calendarId) => setCalendarCfg({ ...calendarCfg, defaultCalendarId: calendarId })}
                />
                <TouchableOpacity
                  style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                  onPress={() => handleSavePatch({ calendar: calendarCfg })}
                  disabled={isSaving}
                >
                  <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Route engine — self-hosted OSRM must already be deployed and
                configured on the SERVER (OSRM_BASE_URL, ROUTE_ENGINE_PROVIDER)
                for this to actually take effect; enabling it here without
                that in place silently keeps falling back to the
                straight-line haversine engine (see VisitRouteService's own
                osrmConfigured check — both this business preference AND the
                server capability must agree). */}
            <View style={styles.formCard}>
              <Text style={styles.sectionTitle}>{t('visitsSetup.sections.routing')}</Text>
              <View style={styles.switchRow}>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.useRoadRouting')}</Text>
                <Switch
                  value={routing.engineProvider === 'osrm'}
                  onValueChange={(v) => setRouting({ ...routing, engineProvider: v ? 'osrm' : 'haversine' })}
                  trackColor={{ true: colors.primary as string, false: colors.border as string }}
                  thumbColor={colors.white as string}
                />
              </View>
              <Text style={styles.formHint}>{t('visitsSetup.fields.useRoadRoutingHint')}</Text>
              <TouchableOpacity
                style={[styles.saveSectionButton, isSaving && styles.disabledButton]}
                onPress={() => handleSavePatch({ routing })}
                disabled={isSaving}
              >
                <Text style={styles.saveSectionButtonText}>{t('visitsSetup.save')}</Text>
              </TouchableOpacity>
            </View>

            {/* Disable */}
            <TouchableOpacity
              style={styles.disableButton}
              onPress={() => setShowDisableConfirm(true)}
              disabled={isSaving}
            >
              <Text style={styles.disableButtonText}>{t('visitsSetup.disableButton')}</Text>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
      </View>

      {eligibility && (
        <BusinessUserPicker
          visible={showUserPicker}
          onClose={() => setShowUserPicker(false)}
          onConfirm={(ids) => {
            setEligibility({ ...eligibility, eligibleUserIds: ids });
            setShowUserPicker(false);
          }}
          mode="multiple"
          source="users"
          selectedUserIds={eligibility.eligibleUserIds}
          title={t('visitsSetup.fields.selectUsers')}
        />
      )}

      {eligibility && (
        <BusinessUserPicker
          visible={showDeptPicker}
          onClose={() => setShowDeptPicker(false)}
          onConfirm={(ids) => {
            setEligibility({ ...eligibility, eligibleDepartmentIds: ids });
            setShowDeptPicker(false);
          }}
          mode="multiple"
          source="departments"
          selectedUserIds={eligibility.eligibleDepartmentIds}
          title={t('visitsSetup.fields.selectDepartments')}
        />
      )}

      {editingOutcome && (
        <BusinessUserPicker
          visible={showAssignDeptPicker}
          onClose={() => setShowAssignDeptPicker(false)}
          onConfirm={(ids) => {
            setEditingOutcome({
              ...editingOutcome,
              action: { type: 'assign', config: { defaultDepartmentId: ids[0] } },
            });
            setShowAssignDeptPicker(false);
          }}
          mode="single"
          source="departments"
          title={t('visitsSetup.fields.selectDepartments')}
        />
      )}

      <Modal
        visible={showOutcomeModal}
        onClose={() => {
          setShowOutcomeModal(false);
          setEditingOutcome(null);
        }}
        title={t('visitsSetup.outcomes.editTitle')}
        size="large"
        scrollable
        footer={
          <View style={styles.modalFooterRow}>
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => {
                setShowOutcomeModal(false);
                setEditingOutcome(null);
              }}
            >
              <Text style={styles.modalCancelButtonText}>{t('visitsSetup.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalSaveButton} onPress={handleSaveOutcome}>
              <Text style={styles.modalSaveButtonText}>{t('visitsSetup.save')}</Text>
            </TouchableOpacity>
          </View>
        }
      >
        {editingOutcome && (
          <View>
            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.label')}</Text>
            <TextInput
              style={styles.input}
              value={editingOutcome.label}
              onChangeText={(v) => setEditingOutcome({ ...editingOutcome, label: v })}
            />
            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.labelAr')}</Text>
            <TextInput
              style={styles.input}
              value={editingOutcome.labelAr || ''}
              onChangeText={(v) => setEditingOutcome({ ...editingOutcome, labelAr: v || undefined })}
            />

            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.icon')}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.iconChipsScroll}>
              {ICON_CHOICES.map((icon) => {
                const active = editingOutcome.icon === icon;
                return (
                  <TouchableOpacity
                    key={icon}
                    style={[styles.iconChip, active && styles.iconChipActive]}
                    onPress={() => setEditingOutcome({ ...editingOutcome, icon })}
                  >
                    <Ionicons name={icon as any} size={18} color={active ? (colors.white as string) : (colors.text.secondary as string)} />
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.color')}</Text>
            <TextInput
              style={styles.input}
              placeholder="#FF9500"
              value={editingOutcome.color || ''}
              onChangeText={(v) => setEditingOutcome({ ...editingOutcome, color: v })}
            />

            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.resultType')}</Text>
            <View style={styles.segmentedRow}>
              {(['success', 'neutral', 'failure'] as const).map((rt) => {
                const active = editingOutcome.resultType === rt;
                return (
                  <TouchableOpacity
                    key={rt}
                    style={[styles.segmentButton, active && styles.segmentButtonActive]}
                    onPress={() => setEditingOutcome({ ...editingOutcome, resultType: rt })}
                  >
                    <Text style={[styles.segmentButtonText, active && styles.segmentButtonTextActive]}>
                      {resultTypeLabel(rt)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={styles.switchRow}>
              <Text style={styles.formLabel}>{t('visitsSetup.outcomes.active')}</Text>
              <Switch
                value={editingOutcome.isActive}
                onValueChange={(v) => setEditingOutcome({ ...editingOutcome, isActive: v })}
                trackColor={{ true: colors.primary as string, false: colors.border as string }}
                thumbColor={colors.white as string}
              />
            </View>

            <Text style={styles.formLabel}>{t('visitsSetup.outcomes.actionType')}</Text>
            <View style={styles.segmentedRow}>
              {([undefined, 'reschedule', 'reminder', 'assign', 'note'] as const).map((type) => {
                const active = (editingOutcome.action?.type ?? undefined) === type;
                return (
                  <TouchableOpacity
                    key={type ?? 'none'}
                    style={[styles.segmentButton, active && styles.segmentButtonActive]}
                    onPress={() =>
                      setEditingOutcome({
                        ...editingOutcome,
                        action: type ? emptyOutcomeAction(type) : undefined,
                      })
                    }
                  >
                    <Text style={[styles.segmentButtonText, active && styles.segmentButtonTextActive]}>
                      {actionTypeLabel(type)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {editingOutcome.action?.type === 'reschedule' && (
              <>
                <Text style={styles.formLabel}>{t('visitsSetup.fields.expectedDurationMinutes')}</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(editingOutcome.action.config?.defaultOffsetHours ?? '')}
                  onChangeText={(v) =>
                    setEditingOutcome({
                      ...editingOutcome,
                      action: { type: 'reschedule', config: { defaultOffsetHours: Number(v) || 0 } },
                    })
                  }
                />
              </>
            )}
            {editingOutcome.action?.type === 'reminder' && (
              <TextInput
                style={styles.input}
                keyboardType="number-pad"
                value={String(editingOutcome.action.config?.defaultOffsetMinutes ?? '')}
                onChangeText={(v) =>
                  setEditingOutcome({
                    ...editingOutcome,
                    action: { type: 'reminder', config: { defaultOffsetMinutes: Number(v) || 0 } },
                  })
                }
              />
            )}
            {editingOutcome.action?.type === 'assign' && (
              <TouchableOpacity style={styles.pickerButton} onPress={() => setShowAssignDeptPicker(true)}>
                <Text style={styles.pickerButtonText}>{t('visitsSetup.fields.selectDepartments')}</Text>
              </TouchableOpacity>
            )}
            {editingOutcome.action?.type === 'note' && (
              <>
                <TextInput
                  style={styles.input}
                  placeholder={t('visitsSetup.outcomes.label')}
                  value={editingOutcome.action.config?.placeholder ?? ''}
                  onChangeText={(v) =>
                    setEditingOutcome({
                      ...editingOutcome,
                      action: { type: 'note', config: { ...editingOutcome.action?.config, placeholder: v } },
                    })
                  }
                />
                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('visitsSetup.outcomes.active')}</Text>
                  <Switch
                    value={!!editingOutcome.action.config?.required}
                    onValueChange={(v) =>
                      setEditingOutcome({
                        ...editingOutcome,
                        action: { type: 'note', config: { ...editingOutcome.action?.config, required: v } },
                      })
                    }
                    trackColor={{ true: colors.primary as string, false: colors.border as string }}
                    thumbColor={colors.white as string}
                  />
                </View>
              </>
            )}
          </View>
        )}
      </Modal>

      <ConfirmationModal
        visible={!!outcomeToRemove}
        onClose={() => setOutcomeToRemove(null)}
        onConfirm={handleRemoveOutcome}
        title={t('visitsSetup.outcomes.removeConfirmTitle')}
        message={t('visitsSetup.outcomes.removeConfirmMessage')}
        type="warning"
      />

      <ConfirmationModal
        visible={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={handleDisable}
        title={t('visitsSetup.disableConfirmTitle')}
        message={t('visitsSetup.disableConfirmMessage')}
        type="warning"
        loading={isSaving}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  formCard: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.lg,
  },
  formLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  formHint: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary as string,
    marginBottom: spacing.sm,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
    marginBottom: spacing.sm,
  },
  sectionHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.md },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
  },
  sectionSubtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary as string,
    marginBottom: spacing.md,
  },

  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
  },
  lockedRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm },
  lockedRowText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  pickerButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  pickerButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  saveSectionButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    marginTop: spacing.md,
  },
  saveSectionButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },

  primaryButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginTop: spacing.md,
  },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },

  disableButton: {
    borderWidth: 1,
    borderColor: colors.status.error as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  disableButtonText: { color: colors.status.error as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },

  addOutcomeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  addOutcomeButtonText: { color: colors.primary as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },

  outcomeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
    gap: spacing.sm,
  },
  outcomeSwatch: { width: 12, height: 12, borderRadius: 6 },
  outcomeIcon: {},
  outcomeInfo: { flex: 1 },
  outcomeLabel: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  outcomeLabelInactive: { color: colors.text.secondary as string, textDecorationLine: 'line-through' },
  outcomeMeta: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: 2 },
  outcomeReorderCol: { alignItems: 'center', gap: 2 },

  iconChipsScroll: { marginBottom: spacing.sm },
  iconChip: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: colors.border as string,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  iconChipActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },

  segmentedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  segmentButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  segmentButtonActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentButtonTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  modalFooterRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  modalCancelButton: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: 8, borderWidth: 1, borderColor: colors.border as string },
  modalCancelButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  modalSaveButton: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: 8, backgroundColor: colors.primary as string },
  modalSaveButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white as string },
});
